import React, { useRef } from 'react';
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import Input from '@mui/material/Input';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import styled from "styled-components";
import { InputAdornment, debounce } from '@mui/material';

import Icn from '../../components/Icn';
import Product from "../../components/Product";
import CategoryTabs from '../../components/CategoryTabs';
import { addOrDeleteQueryParams, getQueryValue } from "../../domain/queryHandler";
import { colors } from "../../maps/colors";
import { api } from "../../maps/data.js";
import useInitialRender from '../../components/hooks/useInitialRender';

const ProductsListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
  gap: 12px;
`;

const INITIAL_PAGE = 0;

const getCategories = async () => {
    const categories_res = await api({
        type: 'getCategoriesWithActiveProductsOnly'
    });
    return categories_res;
};

export default function InventoryPage(props) {
    const history = useNavigate();
    const location = useLocation();
    const initialRender = useInitialRender();

    const searchInputRef = useRef();

    const [searchQuery, setSearchQuery] = useState('');
    const [products, s_products] = useState([]);
    const [categories, s_categories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [callingTheApi, setCallingTheApi] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [total, setTotal] = useState(0);
    const scrollRef = useRef();

    const debouncedOnChange = useRef(debounce(async (e) => {
        scrollRef.current.scrollTo(0, 0);
        const searchQuery = e.target.value;
        setSearchQuery(searchQuery);
        setCurrentPage(INITIAL_PAGE);
    }, 300)).current;

    const [sentryRef] = useInfiniteScroll({
        callingTheApi,
        hasNextPage,
        onLoadMore: () => getProducts(selectedCategory, currentPage, searchQuery)
    });
    const changeCategory = (selectedCategory) => {
        searchInputRef.current && clearSearchInput();
        setSelectedCategory(selectedCategory);

        const urlToPush = addOrDeleteQueryParams(location, [
            { categoryId: selectedCategory._id }
        ]);

        changeUrl(urlToPush);
        getProducts(selectedCategory, INITIAL_PAGE, '');
        setCurrentPage(INITIAL_PAGE);
    };

    const clearSearchInput = () => {
        setSearchQuery('')
        searchInputRef.current.value = '';
    }

    const changeUrl = (query) => {
        history({ search: query });
    }

    async function getProducts(category, page, searchQuery = '') {
        if (callingTheApi) return;
        setCallingTheApi(true);

        const categoryId = searchQuery ? null : category?._id;
        let productsPage = currentPage;

        if (Number.isInteger(page)) {
            productsPage = page;
        }

        const products = await api({
            type: 'getInventoryProducts',
            params: `?search=${searchQuery}`,
            data: { page: productsPage, categoryId }
        });

        if (page === 0) {
            s_products(products);
            setHasNextPage(true);
        } else {
            s_products(prev => [...prev, ...products]);
            if (products.length < 25) {
                setHasNextPage(false);
            }
        }

        setTotal(total);
        setCurrentPage(currPage => currPage + 1);
        setCallingTheApi(false);
    }

    const refreshCategories = async () => {
        const categories = await getCategories();
        s_categories(categories);
        return categories;
    }

    const getMetadata = async () => {
        const categories = await refreshCategories();
        const categoryIdFromQuery = getQueryValue(location, 'categoryId');

        if (categories.length > 0) {
            const initialCategory = categoryIdFromQuery
                ? categories.find(category => category._id === categoryIdFromQuery)
                : categories[0];
            setSelectedCategory(initialCategory);
            getProducts(initialCategory, INITIAL_PAGE, searchQuery);
        }
    }

    useEffect(() => {
        if (!initialRender) {
            const categoryIdFromQuery = getQueryValue(location, 'categoryId');
            const category = searchQuery
                ? {}
                : categories.find(category => category._id === categoryIdFromQuery)
                || categories[0]
            setSelectedCategory(category)
            getProducts(category, INITIAL_PAGE, searchQuery);
        }
    }, [searchQuery]);

    // Initial
    useEffect(() => { getMetadata() }, []);

    return (
        <div style={{ height: '100%' }}>
            {/* Page Content */}
            <Box sx={{ flexGrow: 1, height: '100%', bgcolor: 'background.paper', display: 'flex' }}>
                <CategoryTabs
                    selectedCategory={selectedCategory}
                    onChange={changeCategory}
                    categories={categories}
                    setCategories={s_categories}
                    isLoading={callingTheApi}
                    hasProducts={products.length > 0}
                    readonly
                />

                <div ref={scrollRef} style={{ width: '100%', marginTop: 5, overflowY: 'scroll' }}>
                    <div style={{ display: 'flex', alignItems: 'center', position: 'sticky', top: 0, zIndex: 999, background: colors.WHITE }}>
                        {/* Search Input */}
                        <span style={{ display: 'flex', flex: 1, justifyContent: 'end', padding: 16 }}>
                            <Input
                                inputRef={searchInputRef}
                                onChange={debouncedOnChange}
                                startAdornment={<InputAdornment position="start"><Icn>glassDetails</Icn></InputAdornment>}
                                placeholder='Search...'
                            />
                        </span>
                    </div>

                    <ProductsListWrapper>
                        {products.map((product) => <div key={product._id}>
                            <Product
                                isActive
                                readonly
                                {...product}
                            />
                            <div style={{ position: 'relative', top: '-25vh' }} ref={sentryRef}></div>
                        </div>
                        )}
                    </ProductsListWrapper>
                </div>
            </Box>
        </div>
    );
}